import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null }

/** Last-resort fallback so a render crash shows a reload prompt instead of a blank page. */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="flex min-h-screen items-center justify-center bg-amber-50 px-4">
        <div className="w-full max-w-sm rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
          <p className="text-base font-semibold text-stone-900">Something went wrong</p>
          <p className="mt-1 text-xs text-stone-500 sm:text-sm">Please reload the page. If it keeps happening, go back to the home page.</p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="h-11 rounded-full bg-primary-600 text-sm font-semibold text-white hover:bg-primary-700"
            >
              Reload
            </button>
            <a href="/" className="text-xs font-semibold text-primary-700 hover:underline sm:text-sm">
              Go to home
            </a>
          </div>
        </div>
      </div>
    )
  }
}
